import { useState, useEffect, useCallback, useRef } from "react";
import React from "react";

const API = "https://smart-travel-backend-production-aedc.up.railway.app/api/v1";
console.log("API =", API);

const token = {
  get: () => localStorage.getItem("access_token"),
  set: (t) => localStorage.setItem("access_token", t),
  getRefresh: () => localStorage.getItem("refresh_token"),
  setRefresh: (t) => localStorage.setItem("refresh_token", t),
  clear: () => { localStorage.removeItem("access_token"); localStorage.removeItem("refresh_token"); },
};

async function refreshAccessToken() {
  const refresh = token.getRefresh();
  if (!refresh) return false;
  try {
    const res = await fetch(`${API}/auth/refresh`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({refresh_token:refresh}) });
    if (!res.ok) return false;
    const data = await res.json();
    token.set(data.access_token); token.setRefresh(data.refresh_token);
    return true;
  } catch { return false; }
}

async function api(path, opts = {}) {
  const headers = { "Content-Type":"application/json", ...(opts.headers||{}) };
  if (token.get()) headers["Authorization"] = `Bearer ${token.get()}`;
  let res = await fetch(`${API}${path}`, { ...opts, headers });
  if (res.status === 401 && !path.includes("/auth/login") && !path.includes("/auth/register")) {
    const refreshed = await refreshAccessToken();
    if (refreshed) { headers["Authorization"] = `Bearer ${token.get()}`; res = await fetch(`${API}${path}`, { ...opts, headers }); }
    else { token.clear(); window.location.reload(); throw new Error("Session expired"); }
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
}

const CROWD_COLOR = { low:"#10b981", medium:"#f59e0b", high:"#ef4444" };
const TYPE_EMOJI  = { beach:"🏖️", hill:"⛰️", mountain:"🏔️", heritage:"🏛️", spiritual:"🕌", city:"🌆", adventure:"🧗", wildlife:"🦁" };
const MONTHS      = ["january","february","march","april","may","june","july","august","september","october","november","december"];
const VALID_TYPES = ["beach","hill","mountain","heritage","spiritual","city","adventure","wildlife"];
const STATE_EMOJI = {
  "Goa":"🏖️","Kerala":"🌴","Rajasthan":"🏜️","Himachal Pradesh":"🏔️","Uttarakhand":"⛰️",
  "Jammu and Kashmir":"❄️","Tamil Nadu":"🛕","Karnataka":"🌿","Maharashtra":"🌆",
  "West Bengal":"🐯","Madhya Pradesh":"🐘","Odisha":"🏛️","Gujarat":"🦁",
  "Arunachal Pradesh":"🏔️","Mizoram":"🌄","Ladakh":"🦅","Uttar Pradesh":"🕌",
  "Delhi":"🗺️","Andaman and Nicobar Islands":"🐠","Daman and Diu":"⛵",
};

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type="info") => {
    const id = Date.now();
    setToasts(t => [...t, {id,msg,type}]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);
  return { toasts, add };
}

function ToastContainer({ toasts }) {
  return (
    <div style={{position:"fixed",bottom:"2rem",right:"2rem",zIndex:9999,display:"flex",flexDirection:"column",gap:"0.75rem"}}>
      {toasts.map(t=>(
        <div key={t.id} style={{background:t.type==="success"?"#065f46":t.type==="error"?"#991b1b":"#1a1a4e",color:"#fff",padding:"0.875rem 1.25rem",borderRadius:12,fontSize:"0.875rem",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",display:"flex",alignItems:"center",gap:"0.625rem",maxWidth:320,animation:"slideIn 0.3s ease"}}>
          {t.type==="success"?"✓":t.type==="error"?"✕":"ℹ"} {t.msg}
        </div>
      ))}
    </div>
  );
}

const CSS = (dark) => `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:${dark?"#0d0d1a":"#fdf8f0"};
  --bg2:${dark?"#141425":"#ffffff"};
  --bg3:${dark?"#1a1a2e":"#f5f0e8"};
  --indigo:#1a1a4e;
  --indigo-mid:#2d2d7a;
  --saffron:#f4a535;
  --saffron2:#fbbf24;
  --teal:#0d9488;
  --rose:#e11d48;
  --text:${dark?"#e2e8f0":"#1c1917"};
  --text-mid:${dark?"#94a3b8":"#57534e"};
  --text-light:${dark?"#64748b":"#a8a29e"};
  --border:${dark?"rgba(255,255,255,0.08)":"#e8e0d5"};
  --shadow-sm:0 2px 8px rgba(0,0,0,${dark?0.4:0.08});
  --shadow-md:0 8px 32px rgba(0,0,0,${dark?0.5:0.12});
  --shadow-lg:0 20px 60px rgba(0,0,0,${dark?0.6:0.18});
  --radius:16px;
}
html{scroll-behavior:smooth}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);min-height:100vh;overflow-x:hidden;transition:background 0.35s,color 0.35s}
::-webkit-scrollbar{width:5px}
::-webkit-scrollbar-track{background:var(--bg)}
::-webkit-scrollbar-thumb{background:var(--indigo-mid);border-radius:3px}
@keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes slideIn{from{transform:translateX(110%);opacity:0}to{transform:translateX(0);opacity:1}}
@keyframes floatBlob{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-30px) scale(1.05)}}
@keyframes fly{0%{left:-160px;top:38%;opacity:0;transform:rotate(-6deg)}10%{opacity:1}88%{opacity:1}100%{left:calc(100% + 160px);top:18%;opacity:0;transform:rotate(6deg)}}
@keyframes fly2{0%{left:-100px;top:62%;opacity:0;transform:rotate(-4deg)}15%{opacity:0.65}85%{opacity:0.65}100%{left:calc(100% + 100px);top:48%;opacity:0;transform:rotate(4deg)}}
@keyframes cloud{0%{left:-250px}100%{left:calc(100% + 250px)}}
@keyframes countUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
.navbar{position:sticky;top:0;z-index:200;background:${dark?"rgba(13,13,26,0.96)":"rgba(26,26,78,0.97)"};backdrop-filter:blur(24px);border-bottom:1px solid rgba(244,165,53,0.15);min-height:64px;padding:0 1rem;display:flex;align-items:center;justify-content:space-between;gap:0.5rem;flex-wrap:nowrap}
.brand{font-family:'Cormorant Garamond',serif;font-size:1.45rem;font-weight:700;color:var(--saffron);cursor:pointer;display:flex;align-items:center;gap:0.4rem;white-space:nowrap}
.brand span{color:#fff;font-weight:300}
.nav-links{display:flex;align-items:center;gap:0.1rem;overflow-x:auto;flex-shrink:1;min-width:0}
.nav-links::-webkit-scrollbar{display:none}
.nav-btn{background:none;border:none;color:rgba(255,255,255,0.72);font-family:'DM Sans',sans-serif;font-size:0.72rem;font-weight:500;padding:0.35rem 0.45rem;border-radius:8px;cursor:pointer;transition:all 0.2s;white-space:nowrap}
.nav-btn:hover{color:#fff;background:rgba(255,255,255,0.1)}
.nav-btn.active{color:var(--saffron);background:rgba(244,165,53,0.12)}
.nav-cta{background:var(--saffron);color:var(--indigo);font-weight:700;border:none;padding:0.45rem 1.1rem;border-radius:8px;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:0.82rem;transition:all 0.2s}
.nav-cta:hover{background:var(--saffron2);transform:translateY(-1px)}
.dark-btn{background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.15);color:rgba(255,255,255,0.85);border-radius:100px;padding:0.3rem 0.75rem;font-size:0.8rem;cursor:pointer;display:flex;align-items:center;gap:0.35rem;transition:all 0.2s;white-space:nowrap}
.dark-btn:hover{background:rgba(255,255,255,0.15)}
.user-pill{display:flex;align-items:center;gap:0.5rem;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.15);border-radius:100px;padding:0.3rem 0.75rem 0.3rem 0.4rem;cursor:pointer;transition:all 0.2s}
.user-pill:hover{background:rgba(255,255,255,0.15)}
.user-av{width:26px;height:26px;border-radius:50%;background:linear-gradient(135deg,var(--saffron),var(--teal));display:flex;align-items:center;justify-content:center;font-weight:700;font-size:0.7rem;color:#fff;flex-shrink:0}
.user-name{color:#fff;font-size:0.78rem;font-weight:500}
.hero{position:relative;overflow:hidden;background:linear-gradient(135deg,#080816 0%,#1a1a4e 50%,#0a3d38 100%);min-height:620px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:5rem 2rem 4rem}
.blob{position:absolute;border-radius:50%;filter:blur(90px);opacity:0.18;pointer-events:none}
.b1{width:550px;height:550px;background:var(--saffron);top:-120px;left:-120px;animation:floatBlob 9s ease-in-out infinite}
.b2{width:450px;height:450px;background:var(--teal);bottom:-100px;right:-100px;animation:floatBlob 11s ease-in-out infinite;animation-delay:-4s}
.b3{width:350px;height:350px;background:var(--rose);top:50%;left:55%;transform:translate(-50%,-50%);animation:floatBlob 7s ease-in-out infinite;animation-delay:-2s}
.plane-wrap{position:absolute;inset:0;pointer-events:none;overflow:hidden}
.plane{position:absolute;font-size:2.8rem;animation:fly 14s linear infinite}
.plane2{position:absolute;font-size:2rem;animation:fly2 20s linear infinite;animation-delay:-8s}
.cloud-el{position:absolute;font-size:3.5rem;opacity:0.06;animation:cloud 28s linear infinite;pointer-events:none}
.c1{top:12%;animation-duration:22s}
.c2{top:52%;font-size:2.5rem;animation-duration:32s;animation-delay:-12s}
.c3{top:75%;animation-duration:26s;animation-delay:-6s}
.hero-inner{position:relative;z-index:1;max-width:780px}
.eyebrow{display:inline-flex;align-items:center;gap:0.5rem;background:rgba(244,165,53,0.15);border:1px solid rgba(244,165,53,0.3);color:var(--saffron);font-size:0.72rem;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;padding:0.375rem 1rem;border-radius:100px;margin-bottom:1.5rem;animation:fadeUp 0.5s ease}
.hero-title{font-family:'Cormorant Garamond',serif;font-size:clamp(2.6rem,6.5vw,5.2rem);font-weight:700;line-height:1.08;color:#fff;margin-bottom:1.25rem;animation:fadeUp 0.6s ease 0.1s both}
.hero-title em{font-style:italic;color:var(--saffron)}
.hero-sub{font-size:1.1rem;color:rgba(255,255,255,0.62);line-height:1.75;max-width:500px;margin:0 auto 2.5rem;font-weight:300;animation:fadeUp 0.6s ease 0.2s both}
.hero-cta{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;animation:fadeUp 0.6s ease 0.3s both}
.btn-primary{background:var(--saffron);color:var(--indigo);border:none;font-family:'DM Sans',sans-serif;font-size:1rem;font-weight:700;padding:0.9rem 2.1rem;border-radius:12px;cursor:pointer;transition:all 0.25s;display:inline-flex;align-items:center;gap:0.5rem}
.btn-primary:hover{background:var(--saffron2);transform:translateY(-2px);box-shadow:0 8px 28px rgba(244,165,53,0.45)}
.btn-outline{background:transparent;color:#fff;border:1.5px solid rgba(255,255,255,0.28);font-family:'DM Sans',sans-serif;font-size:1rem;font-weight:500;padding:0.9rem 2.1rem;border-radius:12px;cursor:pointer;transition:all 0.25s}
.btn-outline:hover{border-color:rgba(255,255,255,0.65);background:rgba(255,255,255,0.07)}
.hero-stats{position:relative;z-index:1;display:flex;gap:2.5rem;justify-content:center;flex-wrap:wrap;margin-top:3.5rem;padding-top:2.5rem;border-top:1px solid rgba(255,255,255,0.1);animation:fadeUp 0.6s ease 0.4s both}
.stat-item{text-align:center}
.stat-num{font-family:'Cormorant Garamond',serif;font-size:2.4rem;font-weight:700;color:var(--saffron);animation:countUp 0.8s ease}
.stat-lbl{font-size:0.72rem;color:rgba(255,255,255,0.48);text-transform:uppercase;letter-spacing:0.1em;margin-top:0.2rem}
.feat-section{background:var(--bg2);padding:4.5rem 2rem}
.feat-inner{max-width:1200px;margin:0 auto}
.feat-title{font-family:'Cormorant Garamond',serif;font-size:2.4rem;font-weight:700;color:var(--indigo);text-align:center;margin-bottom:0.5rem}
.feat-sub{color:var(--text-mid);text-align:center;margin-bottom:2.5rem;font-size:0.95rem}
.feat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:1.25rem}
.feat-card{background:var(--bg3);border-radius:var(--radius);padding:2rem 1.5rem;border:1px solid var(--border);text-align:center;transition:all 0.3s;animation:fadeUp 0.5s ease both}
.feat-card:hover{transform:translateY(-5px);box-shadow:var(--shadow-md);border-color:var(--saffron)}
.feat-icon{font-size:2.8rem;margin-bottom:1rem}
.feat-name{font-family:'Cormorant Garamond',serif;font-size:1.15rem;font-weight:700;color:var(--indigo);margin-bottom:0.5rem}
.feat-desc{font-size:0.82rem;color:var(--text-mid);line-height:1.65}
.types-section{padding:3rem 2rem;background:var(--bg)}
.types-inner{max-width:1200px;margin:0 auto;text-align:center}
.types-row{display:flex;gap:0.75rem;flex-wrap:wrap;justify-content:center;margin-top:1.5rem}
.type-pill{background:var(--bg2);border:1.5px solid var(--border);border-radius:100px;padding:0.6rem 1.2rem;font-size:0.88rem;font-weight:500;color:var(--text-mid);cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:0.5rem}
.type-pill:hover{border-color:var(--saffron);color:var(--text);transform:translateY(-2px);box-shadow:var(--shadow-sm)}
.page{max-width:1280px;margin:0 auto;padding:2.5rem 2rem}
.page-top{display:flex;align-items:center;gap:1rem;margin-bottom:1.75rem;flex-wrap:wrap}
.back-btn{background:var(--bg2);border:1.5px solid var(--border);color:var(--text-mid);border-radius:10px;padding:0.45rem 1rem;font-family:'DM Sans',sans-serif;font-size:0.82rem;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:0.4rem;transition:all 0.2s}
.back-btn:hover{border-color:var(--indigo);color:var(--text)}
.sec-head{font-family:'Cormorant Garamond',serif;font-size:2rem;font-weight:700;color:var(--indigo);margin-bottom:0.2rem}
.sec-sub{color:var(--text-mid);font-size:0.92rem;margin-bottom:1.75rem}
.fbar{background:var(--bg2);border-radius:var(--radius);box-shadow:var(--shadow-md);padding:1.5rem 2rem;margin-bottom:2.5rem;border:1px solid var(--border);animation:fadeUp 0.4s ease}
.fgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:1rem;align-items:end}
.flabel{font-size:0.7rem;font-weight:600;color:var(--text-mid);text-transform:uppercase;letter-spacing:0.07em;margin-bottom:0.4rem;display:block}
.finput,.fselect{border:1.5px solid var(--border);border-radius:10px;padding:0.6rem 0.875rem;font-family:'DM Sans',sans-serif;font-size:0.88rem;color:var(--text);background:var(--bg);outline:none;transition:border-color 0.2s;width:100%}
.finput:focus,.fselect:focus{border-color:var(--indigo-mid)}
.fbtn{background:var(--indigo);color:#fff;border:none;border-radius:10px;padding:0.6rem 1.4rem;font-family:'DM Sans',sans-serif;font-size:0.88rem;font-weight:600;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:0.5rem;white-space:nowrap}
.fbtn:hover{background:var(--indigo-mid);transform:translateY(-1px)}
.fbtn:disabled{opacity:0.6;cursor:not-allowed;transform:none}
.dual-slider-wrap{position:relative;padding:0.5rem 0}
.dual-labels{display:flex;justify-content:space-between;font-size:0.72rem;color:var(--text-mid);margin-bottom:0.35rem}
.dual-track{position:relative;height:6px;background:var(--border);border-radius:3px;margin:1.5rem 0 0.5rem}
.dual-fill{position:absolute;height:100%;background:linear-gradient(90deg,var(--teal),var(--saffron));border-radius:3px}
.range-in{position:absolute;width:100%;height:6px;opacity:0;cursor:pointer;top:0;left:0;margin:0}
.dual-vals{display:flex;justify-content:space-between;margin-top:0.5rem}
.dual-val{font-family:'Cormorant Garamond',serif;font-size:1rem;font-weight:700;color:var(--indigo)}
.cards-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(295px,1fr));gap:1.5rem}
.dest-card{background:var(--bg2);border-radius:var(--radius);border:1px solid var(--border);overflow:hidden;box-shadow:var(--shadow-sm);transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);animation:fadeUp 0.5s ease both;cursor:pointer;display:flex;flex-direction:column}
.dest-card:hover{transform:translateY(-7px);box-shadow:var(--shadow-lg)}
.c-head{background:linear-gradient(135deg,#1a1a4e 0%,#2d2d7a 100%);padding:1.5rem;position:relative;overflow:hidden;min-height:118px;display:flex;flex-direction:column;justify-content:space-between}
.c-head::before{content:attr(data-em);position:absolute;right:1rem;top:50%;transform:translateY(-50%);font-size:4.2rem;opacity:0.13}
.type-badge{display:inline-flex;align-items:center;gap:0.4rem;background:rgba(244,165,53,0.2);border:1px solid rgba(244,165,53,0.3);color:var(--saffron);font-size:0.68rem;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;padding:0.22rem 0.6rem;border-radius:100px;width:fit-content}
.c-place{font-family:'Cormorant Garamond',serif;font-size:1.6rem;font-weight:700;color:#fff;margin-top:0.5rem;line-height:1.15}
.c-state{font-size:0.78rem;color:rgba(255,255,255,0.58);margin-top:0.2rem}
.c-body{padding:1.2rem;flex:1;display:flex;flex-direction:column;gap:0.8rem}
.c-desc{font-size:0.855rem;color:var(--text-mid);line-height:1.6}
.chip-row{display:flex;flex-wrap:wrap;gap:0.45rem}
.chip{display:inline-flex;align-items:center;gap:0.28rem;background:var(--bg3);border:1px solid var(--border);border-radius:100px;padding:0.22rem 0.6rem;font-size:0.73rem;color:var(--text-mid);font-weight:500}
.c-foot{padding:0.9rem 1.2rem;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.c-budget{font-family:'Cormorant Garamond',serif;font-size:1.2rem;font-weight:700;color:var(--indigo)}
.c-budget span{font-family:'DM Sans',sans-serif;font-size:0.68rem;font-weight:400;color:var(--text-light)}
.save-btn{background:none;border:none;cursor:pointer;font-size:1.2rem;padding:0.2rem;border-radius:6px;line-height:1;transition:transform 0.2s}
.save-btn:hover{transform:scale(1.25)}
.score-bar{height:4px;background:var(--border);border-radius:2px;overflow:hidden}
.score-fill{height:100%;background:linear-gradient(90deg,var(--teal),var(--saffron));border-radius:2px}
.auth-wrap{min-height:calc(100vh - 64px);display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,var(--bg),var(--bg3));padding:2rem}
.auth-card{background:var(--bg2);border-radius:24px;box-shadow:var(--shadow-lg);width:100%;max-width:440px;padding:2.5rem;border:1px solid var(--border);animation:fadeUp 0.5s ease}
.auth-logo{font-family:'Cormorant Garamond',serif;font-size:1.7rem;font-weight:700;color:var(--indigo);text-align:center;margin-bottom:2rem}
.auth-logo em{font-style:italic;color:var(--teal)}
.auth-title{font-family:'Cormorant Garamond',serif;font-size:1.7rem;font-weight:700;color:var(--indigo);margin-bottom:0.25rem}
.auth-sub{font-size:0.875rem;color:var(--text-mid);margin-bottom:1.75rem}
.fgroup{margin-bottom:1.2rem}
.flb{display:block;font-size:0.75rem;font-weight:600;color:var(--text-mid);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:0.4rem}
.fin{width:100%;padding:0.72rem 1rem;border:1.5px solid var(--border);border-radius:10px;font-family:'DM Sans',sans-serif;font-size:0.93rem;color:var(--text);background:var(--bg);outline:none;transition:all 0.2s}
.fin:focus{border-color:var(--indigo);box-shadow:0 0 0 3px rgba(26,26,78,0.08)}
.fsub{width:100%;padding:0.85rem;background:var(--indigo);color:#fff;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:0.95rem;font-weight:600;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;justify-content:center;gap:0.5rem;margin-top:0.5rem}
.fsub:hover:not(:disabled){background:var(--indigo-mid);transform:translateY(-1px)}
.fsub:disabled{opacity:0.6;cursor:not-allowed}
.aswitch{text-align:center;margin-top:1.4rem;font-size:0.875rem;color:var(--text-mid)}
.aswitch button{background:none;border:none;color:var(--indigo-mid);font-weight:600;cursor:pointer;font-size:0.875rem}
.err-box{background:#fef2f2;border:1.5px solid #fecaca;color:#dc2626;border-radius:10px;padding:0.75rem 1rem;font-size:0.875rem;margin-bottom:1.25rem;display:flex;align-items:flex-start;gap:0.5rem;line-height:1.5}
.otp-row{display:flex;gap:0.6rem;justify-content:center;margin:1.4rem 0}
.otp-box{width:46px;height:54px;text-align:center;font-size:1.5rem;font-weight:700;color:var(--indigo);border:2px solid var(--border);border-radius:10px;font-family:'Cormorant Garamond',serif;outline:none;transition:all 0.2s;background:var(--bg)}
.otp-box:focus{border-color:var(--indigo);box-shadow:0 0 0 3px rgba(26,26,78,0.1)}
.overlay{position:fixed;inset:0;z-index:500;background:rgba(10,10,30,0.72);backdrop-filter:blur(5px);display:flex;align-items:center;justify-content:center;padding:2rem;animation:fadeIn 0.2s ease}
.modal{background:var(--bg2);border-radius:24px;max-width:620px;width:100%;max-height:88vh;overflow-y:auto;box-shadow:var(--shadow-lg);animation:fadeUp 0.3s ease}
.m-hero{background:linear-gradient(135deg,#1a1a4e 0%,#2d2d7a 55%,#0d9488 100%);padding:2.5rem;position:relative;overflow:hidden}
.m-hero::before{content:attr(data-em);position:absolute;right:2rem;top:50%;transform:translateY(-50%);font-size:7rem;opacity:0.1}
.m-place{font-family:'Cormorant Garamond',serif;font-size:2.2rem;font-weight:700;color:#fff}
.m-state{color:rgba(255,255,255,0.6);font-size:0.88rem;margin-top:0.3rem}
.m-body{padding:2rem}
.m-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:0.9rem;margin-bottom:1.75rem}
.m-stat{background:var(--bg3);border-radius:12px;padding:1rem;text-align:center;border:1px solid var(--border)}
.m-val{font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:700;color:var(--indigo)}
.m-lbl{font-size:0.68rem;color:var(--text-light);text-transform:uppercase;letter-spacing:0.08em;margin-top:0.2rem}
.close-x{position:absolute;top:1.2rem;right:1.2rem;background:rgba(255,255,255,0.14);border:none;color:#fff;width:32px;height:32px;border-radius:50%;cursor:pointer;font-size:0.95rem;display:flex;align-items:center;justify-content:center}
.btn-teal{background:var(--teal);color:#fff;border:none;border-radius:10px;padding:0.72rem 1.5rem;font-family:'DM Sans',sans-serif;font-weight:600;font-size:0.88rem;cursor:pointer;transition:all 0.2s}
.btn-teal:hover{background:#0f766e}
.btn-ghost{background:none;border:1.5px solid var(--border);color:var(--text-mid);border-radius:10px;padding:0.72rem 1.5rem;font-family:'DM Sans',sans-serif;font-weight:500;font-size:0.88rem;cursor:pointer}
.state-hero{background:linear-gradient(135deg,#1a1a4e,#2d2d7a 60%,#0d9488);padding:2rem 2rem 1.5rem;margin:-2.5rem -2rem 2rem;border-radius:0 0 20px 20px}
.states-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:1.1rem;margin-bottom:2rem}
.state-card{background:var(--bg2);border-radius:var(--radius);border:1px solid var(--border);padding:1.3rem;cursor:pointer;transition:all 0.25s;display:flex;align-items:center;gap:0.9rem}
.state-card:hover{transform:translateY(-4px);box-shadow:var(--shadow-md);border-color:var(--indigo)}
.s-icon{width:42px;height:42px;border-radius:10px;background:linear-gradient(135deg,var(--indigo),var(--teal));display:flex;align-items:center;justify-content:center;font-size:1.2rem;flex-shrink:0}
.s-name{font-family:'Cormorant Garamond',serif;font-size:1.05rem;font-weight:700;color:var(--indigo)}
.s-count{font-size:0.73rem;color:var(--text-light)}
.sort-row{display:flex;align-items:center;gap:0.55rem;margin-bottom:1.4rem;flex-wrap:wrap}
.sort-lbl{font-size:0.75rem;font-weight:600;color:var(--text-mid);text-transform:uppercase;letter-spacing:0.06em}
.sort-btn{background:none;border:1.5px solid var(--border);border-radius:8px;padding:0.35rem 0.72rem;font-family:'DM Sans',sans-serif;font-size:0.78rem;font-weight:500;color:var(--text-mid);cursor:pointer;transition:all 0.2s}
.sort-btn:hover{border-color:var(--indigo);color:var(--text)}
.sort-btn.on{background:var(--indigo);border-color:var(--indigo);color:#fff}
.note-area{width:100%;padding:0.72rem;border:1.5px solid var(--border);border-radius:8px;font-family:'DM Sans',sans-serif;font-size:0.855rem;resize:vertical;min-height:76px;outline:none;background:var(--bg);color:var(--text)}
.note-save{background:var(--teal);color:#fff;border:none;border-radius:8px;padding:0.45rem 0.9rem;font-size:0.78rem;font-weight:600;cursor:pointer;margin-top:0.45rem}
.prof-card{background:var(--bg2);border-radius:var(--radius);border:1px solid var(--border);padding:2rem;max-width:480px;animation:fadeUp 0.4s ease}
.prof-av{width:70px;height:70px;border-radius:50%;background:linear-gradient(135deg,var(--indigo),var(--teal));display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:2rem;font-weight:700;color:#fff;margin-bottom:1rem}
.empty{text-align:center;padding:4rem 2rem}
.empty-icon{font-size:3.5rem;margin-bottom:1rem}
.empty-title{font-family:'Cormorant Garamond',serif;font-size:1.5rem;font-weight:700;color:var(--indigo);margin-bottom:0.5rem}
.empty-sub{color:var(--text-mid);font-size:0.9rem}
.spinner{width:20px;height:20px;border:2.5px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin 0.7s linear infinite;display:inline-block}
.sp-dark{border-color:rgba(26,26,78,0.18);border-top-color:var(--indigo)}
.sp-center{display:flex;justify-content:center;padding:3rem}
.badge{display:inline-flex;align-items:center;background:var(--indigo);color:#fff;border-radius:100px;padding:0.22rem 0.72rem;font-size:0.78rem;font-weight:600}
@media(max-width:680px){
  .hero{padding:3.5rem 1.25rem 2.5rem;min-height:520px}
  .navbar{padding:0 1rem}
  .page{padding:1.5rem 1rem}
  .m-stats{grid-template-columns:repeat(2,1fr)}
  .fgrid{grid-template-columns:1fr 1fr}
  .brand{font-size:1.2rem}
  .nav-btn{font-size:0.7rem;padding:0.35rem 0.5rem}
}
`;

function DualRange({ min, max, valMin, valMax, onMin, onMax, step=500, prefix="₹" }) {
  const lp = ((valMin-min)/(max-min))*100;
  const rp = ((valMax-min)/(max-min))*100;
  return (
    <div className="dual-slider-wrap">
      <div className="dual-labels"><span>{prefix}{min.toLocaleString()}</span><span>{prefix}{max.toLocaleString()}</span></div>
      <div className="dual-track">
        <div className="dual-fill" style={{left:`${lp}%`,width:`${rp-lp}%`}}/>
        <input type="range" className="range-in" min={min} max={max} step={step} value={valMin} onChange={e=>onMin(Math.min(+e.target.value,valMax-step))} style={{zIndex:valMin>max-step?3:1}}/>
        <input type="range" className="range-in" min={min} max={max} step={step} value={valMax} onChange={e=>onMax(Math.max(+e.target.value,valMin+step))} style={{zIndex:2}}/>
      </div>
      <div className="dual-vals">
        <span className="dual-val">{prefix}{valMin.toLocaleString()}</span>
        <span style={{color:"var(--text-mid)",fontSize:"0.8rem",alignSelf:"center"}}>to</span>
        <span className="dual-val">{prefix}{valMax.toLocaleString()}</span>
      </div>
    </div>
  );
}

function DestCard({ dest, onSave, onUnsave, onClick, delay=0 }) {
  const sp = Math.min(100,((dest.score-8)/12)*100);
  return (
    <div className="dest-card" style={{animationDelay:`${delay}ms`,cursor:"pointer"}} onClick={()=>onClick(dest)}>
      <div className="c-head" data-em={TYPE_EMOJI[dest.type]||"🗺️"}>
        <div className="type-badge">{TYPE_EMOJI[dest.type]} {dest.type}</div>
        <div><div className="c-place">{dest.place}</div><div className="c-state">📍 {dest.state}</div></div>
      </div>
      <div className="c-body">
        <p className="c-desc">{dest.description?.slice(0,88)}{dest.description?.length>88?"…":""}</p>
        <div className="chip-row">
          <span className="chip"><span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:CROWD_COLOR[dest.crowd],marginRight:2}}/>{dest.crowd}</span>
          <span className="chip">🌡 {dest.temperature}°C</span>
          <span className="chip">📅 {dest.days}d</span>
          <span className="chip">🛡 {dest.safety}/10</span>
        </div>
        <div><div style={{fontSize:"0.68rem",color:"var(--text-light)",marginBottom:"0.28rem"}}>Match score</div>
          <div className="score-bar"><div className="score-fill" style={{width:`${sp}%`}}/></div></div>
      </div>
      <div className="c-foot" onClick={e=>e.stopPropagation()}>
        <div className="c-budget">₹{dest.budget?.toLocaleString()} <span>/ person</span></div>
        <div style={{display:"flex",alignItems:"center",gap:"0.65rem"}}>
          <span style={{fontSize:"0.85rem",fontWeight:600}}>★ {dest.rating}</span>
          <button className="save-btn" onClick={e=>{e.stopPropagation();dest.is_saved?onUnsave(dest):onSave(dest);}}>{dest.is_saved?"❤️":"🤍"}</button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ dest, onClose, onSave, onUnsave }) {
  if (!dest) return null;
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="m-hero" data-em={TYPE_EMOJI[dest.type]||"🗺️"} style={{position:"relative"}}>
          <button className="close-x" onClick={onClose}>✕</button>
          <div className="type-badge" style={{marginBottom:"0.7rem"}}>{TYPE_EMOJI[dest.type]} {dest.type}</div>
          <div className="m-place">{dest.place}</div>
          <div className="m-state">📍 {dest.state}</div>
        </div>
        <div className="m-body">
          <p style={{fontSize:"0.92rem",color:"var(--text-mid)",lineHeight:1.78,marginBottom:"1.6rem"}}>{dest.description}</p>
          <div className="m-stats">
            {[["₹"+dest.budget?.toLocaleString(),"Budget"],[dest.days+"d","Duration"],["★ "+dest.rating,"Rating"],[dest.safety+"/10","Safety"],[dest.temperature+"°C","Temp"],[dest.crowd,"Crowd"]].map(([v,l])=>(
              <div key={l} className="m-stat">
                <div className="m-val" style={{fontSize:l==="Crowd"?"0.95rem":undefined,textTransform:"capitalize",color:l==="Crowd"?CROWD_COLOR[dest.crowd]:undefined}}>{v}</div>
                <div className="m-lbl">{l}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:"0.72rem",fontWeight:600,color:"var(--text-light)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.6rem"}}>Best months</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.45rem",marginBottom:"1.5rem"}}>
            {dest.best_months?.split(" ").map(m=><span key={m} className="chip" style={{textTransform:"capitalize"}}>📅 {m}</span>)}
          </div>
          <div style={{display:"flex",gap:"0.65rem"}}>
            <button className="btn-teal" onClick={()=>{dest.is_saved?onUnsave(dest):onSave(dest);onClose();}}>{dest.is_saved?"❤️ Saved":"🤍 Save"}</button>
            <button className="btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OTPView({ email, purpose, onVerified, onBack, toast }) {
  const [digits, setDigits] = useState(["","","","","",""]);
  const [loading, setLoading] = useState(false);
  const upd = (i,v) => { if(!/^\d?$/.test(v)) return; const n=[...digits];n[i]=v;setDigits(n);if(v&&i<5)document.getElementById(`ob${i+1}`)?.focus(); };
  const key = (i,e) => { if(e.key==="Backspace"&&!digits[i]&&i>0)document.getElementById(`ob${i-1}`)?.focus(); };
  const go = async () => {
    const c=digits.join("");if(c.length<6) return;setLoading(true);
    try{await api("/auth/verify-otp",{method:"POST",body:JSON.stringify({email,code:c,purpose})});toast.add("Verified! ✓","success");onVerified();}
    catch(e){toast.add(e.message,"error");}finally{setLoading(false);}
  };
  const resend = async () => { try{await api("/auth/resend-otp",{method:"POST",body:JSON.stringify({email,purpose})});toast.add("OTP resent","success");}catch(e){toast.add(e.message,"error");} };
  return (
    <div className="auth-card">
      <div style={{textAlign:"center",marginBottom:"1rem"}}><div style={{fontSize:"2.5rem"}}>📧</div><div className="auth-title" style={{marginTop:"0.5rem"}}>Check your email</div><p className="auth-sub">Code sent to <strong>{email}</strong></p></div>
      <div className="otp-row">{digits.map((d,i)=><input key={i} id={`ob${i}`} className="otp-box" maxLength={1} value={d} onChange={e=>upd(i,e.target.value)} onKeyDown={e=>key(i,e)} autoFocus={i===0}/>)}</div>
      <button className="fsub" onClick={go} disabled={loading||digits.join("").length<6}>{loading?<span className="spinner"/>:"Verify OTP"}</button>
      <div className="aswitch"><button onClick={resend}>Resend</button>{" · "}<button onClick={onBack}>Back</button></div>
    </div>
  );
}

function ResetPasswordForm({ email, toast, onDone }) {
  const [form, setForm] = useState({password:"",confirm:""});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);  const go = async () => {
    setError("");
    if (!form.password||form.password.length<8){setError("Password must be at least 8 characters.");return;}
    if (form.password!==form.confirm){setError("Passwords do not match.");return;}
    setLoading(true);
    try{
      await api("/auth/set-password",{method:"POST",body:JSON.stringify({email,code:"verified",new_password:form.password})});
      toast.add("Password reset! Please log in.","success");
      onDone();
    }catch(e){
      setError(typeof e.message==="string"?e.message:"Failed to reset password. Please try again.");
    }
    finally{setLoading(false);}
  };
  return (
    <div className="auth-card">
      <div style={{textAlign:"center",marginBottom:"1.5rem"}}>
        <div style={{fontSize:"2rem"}}>🔑</div>
        <div className="auth-title" style={{marginTop:"0.5rem"}}>Set new password</div>
        <p className="auth-sub">Choose a strong password for your account</p>
      </div>
      {error&&<div className="err-box"><span>⚠️</span><span>{error}</span></div>}
      <div className="fgroup">
        <label className="flb">New Password</label>
        <PasswordInput placeholder="Min 8 characters" value={form.password}
  show={showPwd} onToggle={()=>setShowPwd(s=>!s)}
  onChange={e=>{setForm({...form,password:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/>
      </div>
      <div className="fgroup">
        <label className="flb">Confirm New Password</label>
        <PasswordInput placeholder="Repeat your password" value={form.confirm}
  show={showConfirm} onToggle={()=>setShowConfirm(s=>!s)}
  style={{borderColor:form.confirm&&form.password!==form.confirm?"#fca5a5":""}}
  onChange={e=>{setForm({...form,confirm:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/>
        {form.confirm&&form.password!==form.confirm&&<div style={{fontSize:"0.78rem",color:"#dc2626",marginTop:"0.35rem"}}>Passwords do not match</div>}
        {form.confirm&&form.password===form.confirm&&form.confirm.length>=8&&<div style={{fontSize:"0.78rem",color:"#16a34a",marginTop:"0.35rem"}}>✓ Passwords match</div>}
      </div>
      <button className="fsub" onClick={go} disabled={loading||!form.password||!form.confirm||form.password!==form.confirm}>
        {loading?<span className="spinner"/>:"Reset Password →"}
      </button>
    </div>
  );
}

function PasswordInput({ value, onChange, onKeyDown, placeholder, style, show, onToggle }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        className="fin"
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        style={{ paddingRight: "2.8rem", ...style }}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{
          position: "absolute", right: "0.75rem", top: "50%",
          transform: "translateY(-50%)", background: "none",
          border: "none", cursor: "pointer", fontSize: "1.1rem",
          color: "var(--text-mid)", padding: "0.2rem", lineHeight: 1
        }}
      >
        {show ? "🙈" : "👁️"}
      </button>
    </div>
  );
}
function RegisterView({ onSuccess, onLogin, toast }) {
  const [form, setForm] = useState({name:"",email:"",password:""});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("form");
  const [showPwd, setShowPwd] = useState(false);
  const go = async () => {
    setError("");
    if (!form.name||form.name.trim().length<2){setError("Name must be at least 2 characters.");return;}
    if (!form.email||!form.email.includes("@")){setError("Please enter a valid email address.");return;}
    if (!form.password||form.password.length<8){setError("Password must be at least 8 characters.");return;}
    setLoading(true);
    try{await api("/auth/register",{method:"POST",body:JSON.stringify(form)});setStep("otp");}
    catch(e){
      const msg=(e.message||"").toLowerCase();
      if(msg.includes("already")||msg.includes("registered")) setError("This email is already registered. Please sign in.");
      else setError(e.message||"Registration failed. Please try again.");
    }
    finally{setLoading(false);}
  };
  if (step==="otp") return <OTPView email={form.email} purpose="verify_email" onVerified={onSuccess} onBack={()=>setStep("form")} toast={toast}/>;
  return (
    <div className="auth-card">
      <div className="auth-logo">Smart<em>Travel</em></div>
      <div className="auth-title">Create account</div>
      <p className="auth-sub">Start exploring India today</p>
      {error&&<div className="err-box"><span>⚠️</span><span>{error}</span></div>}
      <div className="fgroup"><label className="flb">Full Name</label><input className="fin" type="text" placeholder="Priya Sharma" value={form.name} onChange={e=>{setForm({...form,name:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/></div>
      <div className="fgroup"><label className="flb">Email</label><input className="fin" type="email" placeholder="you@example.com" value={form.email} onChange={e=>{setForm({...form,email:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/></div>
      <div className="fgroup"><label className="flb">Password</label><PasswordInput placeholder="Min 8 characters" value={form.password} show={showPwd} onToggle={()=>setShowPwd(s=>!s)} onChange={e=>{setForm({...form,password:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/></div>      <button className="fsub" onClick={go} disabled={loading||!form.name||!form.email||!form.password}>{loading?<span className="spinner"/>:"Create Account →"}</button>
      <div className="aswitch">Have an account? <button onClick={onLogin}>Sign in</button></div>
    </div>
  );
}

function LoginView({ onSuccess, onRegister, toast }) {
  const [form, setForm] = useState({email:"",password:""});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [fEmail, setFEmail] = useState("");
  const [otpReset, setOtpReset] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const go = async () => {
    setError("");
    if (!form.email||!form.password){setError("Please enter both email and password.");return;}
    setLoading(true);
    try{
      const d=await api("/auth/login",{method:"POST",body:JSON.stringify(form)});
      token.set(d.access_token);token.setRefresh(d.refresh_token);
      toast.add("Welcome back! 🌏","success");onSuccess();
    }catch(e){
      const msg=(e.message||"").toLowerCase();
      if(msg.includes("incorrect")||msg.includes("wrong")||msg.includes("invalid")||msg.includes("password")) setError("Incorrect email or password. Please try again.");
      else if(msg.includes("not found")||msg.includes("no account")||msg.includes("does not exist")) setError("No account found with this email. Please sign up.");
      else if(msg.includes("verified")||msg.includes("verify")) setError("Email not verified. Check your inbox for the OTP or click Resend.");
      else setError(e.message||"Login failed. Please check your details and try again.");
    }
    finally{setLoading(false);}
  };

  const sendForgot = async () => {
    setError("");
    if (!fEmail){setError("Please enter your email address.");return;}
    setLoading(true);
    try{await api("/auth/forgot-password",{method:"POST",body:JSON.stringify({email:fEmail})});setOtpReset(true);}
    catch(e){setError(e.message||"Failed to send reset code.");}
    finally{setLoading(false);}
  };

  const [showReset, setShowReset] = useState(false);

  if(showReset) return(
    <ResetPasswordForm email={fEmail} toast={toast} onDone={()=>{setForgot(false);setOtpReset(false);setShowReset(false);setFEmail("");}}/>
  );

  if(otpReset) return(
    <div className="auth-card">
      <div style={{textAlign:"center",marginBottom:"1rem"}}>
        <div style={{fontSize:"2.5rem"}}>📧</div>
        <div className="auth-title" style={{marginTop:"0.5rem"}}>Check your email</div>
        <p className="auth-sub">Enter the 6-digit code sent to <strong>{fEmail}</strong></p>
      </div>
      <OTPView email={fEmail} purpose="reset_password" onVerified={()=>setShowReset(true)} onBack={()=>setOtpReset(false)} toast={toast}/>
    </div>
  );

  if(forgot) return(
    <div className="auth-card">
      <div style={{textAlign:"center",marginBottom:"1.5rem"}}><div style={{fontSize:"2rem"}}>🔐</div><div className="auth-title" style={{marginTop:"0.5rem"}}>Forgot password</div><p className="auth-sub">We will send a reset code to your email</p></div>
      {error&&<div className="err-box"><span>⚠️</span><span>{error}</span></div>}
      <div className="fgroup"><label className="flb">Email</label><input className="fin" type="email" placeholder="you@example.com" value={fEmail} onChange={e=>{setFEmail(e.target.value);setError("");}} onKeyDown={e=>e.key==="Enter"&&sendForgot()}/></div>
      <button className="fsub" onClick={sendForgot} disabled={loading||!fEmail}>{loading?<span className="spinner"/>:"Send Reset Code"}</button>
      <div className="aswitch"><button onClick={()=>{setForgot(false);setError("");}}>← Back to login</button></div>
    </div>
  );

  return(
    <div className="auth-card">
      <div className="auth-logo">Smart<em>Travel</em></div>
      <div className="auth-title">Welcome back</div>
      <p className="auth-sub">Sign in to continue your journey</p>
      {error&&<div className="err-box"><span style={{flexShrink:0}}>⚠️</span><span>{error}</span></div>}
      <div className="fgroup"><label className="flb">Email</label>
        <input className="fin" type="email" placeholder="you@example.com" value={form.email}
          style={{borderColor:error?"#fca5a5":""}}
          onChange={e=>{setForm({...form,email:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/>
      </div>
      <div className="fgroup"><label className="flb">Password</label>
    <PasswordInput placeholder="Your password" value={form.password}
      show={showPwd} onToggle={()=>setShowPwd(s=>!s)}
      style={{borderColor:error?"#fca5a5":""}}
      onChange={e=>{setForm({...form,password:e.target.value});setError("");}} onKeyDown={e=>e.key==="Enter"&&go()}/>
      </div>
      <div style={{textAlign:"right",marginBottom:"1.25rem"}}>
        <button onClick={()=>{setForgot(true);setError("");}} style={{background:"none",border:"none",color:"var(--teal)",fontSize:"0.83rem",cursor:"pointer",fontWeight:500}}>Forgot password?</button>
      </div>
      <button className="fsub" onClick={go} disabled={loading||!form.email||!form.password}>{loading?<span className="spinner"/>:"Sign In →"}</button>
      <div className="aswitch">No account? <button onClick={onRegister}>Sign up</button></div>
    </div>
  );
}

// ── Map Page ─────────────────────────────────────────────────────────────────
function MapPage({ toast, onBack }) {
  const [places, setPlaces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markersRef = React.useRef([]);

  useEffect(() => {
    // Load Leaflet CSS
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    // Load Leaflet JS
    if (!window.L) {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => setMapReady(true);
      document.head.appendChild(script);
    } else {
      setMapReady(true);
    }
    api("/places").then(d => setPlaces(d.results || [])).catch(e => toast.add(e.message, "error")).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || places.length === 0) return;
    if (mapInstanceRef.current) { mapInstanceRef.current.remove(); }
    const L = window.L;
    const map = L.map(mapRef.current, { center: [22.5, 80], zoom: 5, zoomControl: true });
    mapInstanceRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors", maxZoom: 18
    }).addTo(map);

    const filtered = filter ? places.filter(p => p.type === filter) : places;

    const typeColors = { beach:"#0d9488", hill:"#059669", mountain:"#7c3aed", heritage:"#b45309", spiritual:"#dc2626", city:"#2563eb", adventure:"#ea580c", wildlife:"#65a30d" };

    markersRef.current = filtered.map(place => {
      const color = typeColors[place.type] || "#1a1a4e";
      const icon = L.divIcon({
        className: "",
        html: `<div style="background:${color};width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;cursor:pointer"><span style="transform:rotate(45deg);font-size:13px">${{"beach":"🏖","hill":"⛰","mountain":"🏔","heritage":"🏛","spiritual":"🕌","city":"🌆","adventure":"🧗","wildlife":"🦁"}[place.type]||"📍"}</span></div>`,
        iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32]
      });
      const marker = L.marker([place.lat, place.lon], { icon }).addTo(map);
      marker.bindPopup(`
        <div style="font-family:'DM Sans',sans-serif;min-width:200px;padding:4px">
          <div style="font-family:'Cormorant Garamond',serif;font-size:1.2rem;font-weight:700;color:#1a1a4e;margin-bottom:4px">${place.place}</div>
          <div style="font-size:0.78rem;color:#57534e;margin-bottom:8px">📍 ${place.state}</div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px">
            <span style="background:#f5f0e8;border-radius:100px;padding:2px 8px;font-size:0.72rem">₹${place.budget.toLocaleString()}</span>
            <span style="background:#f5f0e8;border-radius:100px;padding:2px 8px;font-size:0.72rem">★ ${place.rating}</span>
            <span style="background:#f5f0e8;border-radius:100px;padding:2px 8px;font-size:0.72rem">📅 ${place.days}d</span>
          </div>
          <div style="font-size:0.78rem;color:#57534e;line-height:1.5">${place.description?.slice(0,80)}…</div>
        </div>
      `);
      marker.on("click", () => setSelected(place));
      return marker;
    });
  }, [mapReady, places, filter]);

  const TYPE_COLORS = { beach:"#0d9488", hill:"#059669", mountain:"#7c3aed", heritage:"#b45309", spiritual:"#dc2626", city:"#2563eb", adventure:"#ea580c", wildlife:"#65a30d" };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 64px)"}}>
      <div style={{padding:"1rem 2rem",background:"var(--bg2)",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.4rem",fontWeight:700,color:"var(--indigo)"}}>🗺️ India Travel Map</div>
        <div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap",marginLeft:"auto"}}>
          {["", ...VALID_TYPES].map(t => (
            <button key={t} onClick={() => setFilter(t)}
              style={{background:filter===t?(TYPE_COLORS[t]||"var(--indigo)"):"var(--bg3)",color:filter===t?"#fff":"var(--text-mid)",border:`1.5px solid ${filter===t?(TYPE_COLORS[t]||"var(--indigo)"):"var(--border)"}`,borderRadius:"100px",padding:"0.3rem 0.75rem",fontSize:"0.75rem",fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}>
              {t ? `${TYPE_EMOJI[t]} ${t.charAt(0).toUpperCase()+t.slice(1)}` : "🗺 All"}
            </button>
          ))}
        </div>
      </div>
      <div style={{flex:1,position:"relative"}}>
        {loading && <div className="sp-center" style={{position:"absolute",inset:0,zIndex:10,background:"var(--bg)"}}><div className="spinner sp-dark" style={{width:48,height:48,borderWidth:4}}/></div>}
        <div ref={mapRef} style={{width:"100%",height:"100%"}}/>
        {selected && (
          <div style={{position:"absolute",bottom:"2rem",left:"50%",transform:"translateX(-50%)",zIndex:1000,width:"90%",maxWidth:400,background:"var(--bg2)",borderRadius:16,boxShadow:"0 20px 60px rgba(0,0,0,0.3)",border:"1px solid var(--border)",overflow:"hidden",animation:"fadeUp 0.3s ease"}}>
            <div style={{background:`linear-gradient(135deg,#1a1a4e,#2d2d7a)`,padding:"1.25rem",position:"relative"}}>
              <button onClick={()=>setSelected(null)} style={{position:"absolute",top:"0.75rem",right:"0.75rem",background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",width:28,height:28,borderRadius:"50%",cursor:"pointer",fontSize:"0.85rem"}}>✕</button>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.5rem",fontWeight:700,color:"#fff"}}>{selected.place}</div>
              <div style={{color:"rgba(255,255,255,0.65)",fontSize:"0.82rem",marginTop:2}}>📍 {selected.state} · {TYPE_EMOJI[selected.type]} {selected.type}</div>
            </div>
            <div style={{padding:"1rem 1.25rem"}}>
              <div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap",marginBottom:"0.75rem"}}>
                {[["₹"+selected.budget?.toLocaleString(),"Budget"],["★ "+selected.rating,"Rating"],[selected.days+"d","Duration"],[selected.safety+"/10","Safety"]].map(([v,l])=>(
                  <div key={l} style={{background:"var(--bg3)",borderRadius:10,padding:"0.4rem 0.75rem",textAlign:"center",border:"1px solid var(--border)"}}>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,color:"var(--indigo)",fontSize:"0.95rem"}}>{v}</div>
                    <div style={{fontSize:"0.65rem",color:"var(--text-light)",textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
                  </div>
                ))}
              </div>
              <p style={{fontSize:"0.82rem",color:"var(--text-mid)",lineHeight:1.6}}>{selected.description}</p>
              <button className="btn-teal" style={{marginTop:"0.75rem",width:"100%",justifyContent:"center"}} onClick={async()=>{try{await api("/save-place",{method:"POST",body:JSON.stringify({place:selected.place,note:""})});toast.add(selected.place+" saved! ❤️","success");}catch(e){toast.add(e.message,"error");}}}>
                ❤️ Save Destination
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Dashboard Page ────────────────────────────────────────────────────────────
function DashboardPage({ toast, onBack }) {
  const [saved, setSaved] = useState([]);
  const [allPlaces, setAllPlaces] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api("/auth/me").catch(() => null),
      api("/saved-places").catch(() => ({results:[]})),
      api("/places").catch(() => ({results:[]})),
    ]).then(([u, s, a]) => {
      setUser(u);
      setSaved(s.results || []);
      setAllPlaces(a.results || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="sp-center" style={{marginTop:"4rem"}}><div className="spinner sp-dark" style={{width:48,height:48,borderWidth:4}}/></div>;

  const totalBudget = saved.reduce((sum, s) => sum + (s.budget || 0), 0);
  const avgRating   = saved.length ? (saved.reduce((s, p) => s + p.rating, 0) / saved.length).toFixed(1) : 0;
  const avgSafety   = saved.length ? Math.round(saved.reduce((s, p) => s + p.safety, 0) / saved.length) : 0;
  const totalDays   = saved.reduce((s, p) => s + (p.days || 0), 0);

  const typeCount = saved.reduce((acc, p) => { acc[p.type] = (acc[p.type]||0)+1; return acc; }, {});
  const stateCount = saved.reduce((acc, p) => { acc[p.state] = (acc[p.state]||0)+1; return acc; }, {});
  const crowdCount = saved.reduce((acc, p) => { acc[p.crowd] = (acc[p.crowd]||0)+1; return acc; }, {});

  const allTypeCount = allPlaces.reduce((acc, p) => { acc[p.type] = (acc[p.type]||0)+1; return acc; }, {});
  const topType = Object.entries(typeCount).sort((a,b)=>b[1]-a[1])[0];
  const topState = Object.entries(stateCount).sort((a,b)=>b[1]-a[1])[0];
  const maxTypeCount = Math.max(...Object.values(allTypeCount), 1);

  const BAR_COLORS = { beach:"#0d9488", hill:"#059669", mountain:"#7c3aed", heritage:"#b45309", spiritual:"#dc2626", city:"#2563eb", adventure:"#ea580c", wildlife:"#65a30d" };
  const CROWD_C = { low:"#10b981", medium:"#f59e0b", high:"#ef4444" };

  return (
    <div className="page">
      <div className="page-top">
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div>
          <div className="sec-head">📊 Dashboard</div>
          <div className="sec-sub">Your travel stats at a glance{user ? `, ${user.name.split(" ")[0]}` : ""}</div>
        </div>
      </div>

      {saved.length === 0 ? (
        <div className="empty"><div className="empty-icon">📊</div><div className="empty-title">No data yet</div><p className="empty-sub">Save some destinations first to see your personalised dashboard</p></div>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:"1rem",marginBottom:"2rem"}}>
            {[
              ["❤️",saved.length,"Saved Places","total destinations in your wishlist"],
              ["₹",`₹${totalBudget.toLocaleString()}`,"Total Budget","combined cost of all saved trips"],
              ["📅",totalDays+" days","Total Days","if you visited every saved place"],
              ["★",avgRating,"Avg Rating","average rating of saved places"],
              ["🛡",avgSafety+"/10","Avg Safety","average safety score"],
              ["📍",Object.keys(stateCount).length,"States","different states in your wishlist"],
            ].map(([icon,val,label,hint])=>(
              <div key={label} style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",padding:"1.25rem",boxShadow:"var(--shadow-sm)",animation:"fadeUp 0.5s ease both",textAlign:"center"}}>
                <div style={{fontSize:"1.75rem",marginBottom:"0.5rem"}}>{icon}</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.75rem",fontWeight:700,color:"var(--indigo)"}}>{val}</div>
                <div style={{fontWeight:600,fontSize:"0.82rem",color:"var(--text)",marginTop:"0.2rem"}}>{label}</div>
                <div style={{fontSize:"0.72rem",color:"var(--text-light)",marginTop:"0.2rem",lineHeight:1.4}}>{hint}</div>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:"1.5rem",marginBottom:"2rem"}}>
            {/* Destinations by Type bar chart */}
            <div style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",padding:"1.5rem",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.2rem",fontWeight:700,color:"var(--indigo)",marginBottom:"1.25rem"}}>All Destinations by Type</div>
              <div style={{display:"flex",flexDirection:"column",gap:"0.75rem"}}>
                {Object.entries(allTypeCount).sort((a,b)=>b[1]-a[1]).map(([type,count])=>(
                  <div key={type}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.8rem",marginBottom:"0.3rem"}}>
                      <span style={{color:"var(--text)",fontWeight:500}}>{TYPE_EMOJI[type]} {type.charAt(0).toUpperCase()+type.slice(1)}</span>
                      <span style={{color:"var(--text-mid)",fontWeight:600}}>{count}</span>
                    </div>
                    <div style={{height:8,background:"var(--border)",borderRadius:4,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${(count/maxTypeCount)*100}%`,background:BAR_COLORS[type]||"var(--indigo)",borderRadius:4,transition:"width 1s ease"}}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Your saved by type donut-style */}
            <div style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",padding:"1.5rem",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.2rem",fontWeight:700,color:"var(--indigo)",marginBottom:"1.25rem"}}>Your Saved by Type</div>
              {Object.keys(typeCount).length === 0 ? <p style={{color:"var(--text-mid)",fontSize:"0.875rem"}}>No data yet</p> : (
                <div style={{display:"flex",flexDirection:"column",gap:"0.625rem"}}>
                  {Object.entries(typeCount).sort((a,b)=>b[1]-a[1]).map(([type,count])=>(
                    <div key={type} style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                      <div style={{width:36,height:36,borderRadius:10,background:BAR_COLORS[type]||"var(--indigo)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",flexShrink:0}}>{TYPE_EMOJI[type]}</div>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.82rem",marginBottom:3}}>
                          <span style={{fontWeight:500,color:"var(--text)",textTransform:"capitalize"}}>{type}</span>
                          <span style={{fontWeight:700,color:"var(--indigo)"}}>{count}</span>
                        </div>
                        <div style={{height:6,background:"var(--border)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${(count/saved.length)*100}%`,background:BAR_COLORS[type]||"var(--indigo)",borderRadius:3}}/>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Crowd preference */}
            <div style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",padding:"1.5rem",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.2rem",fontWeight:700,color:"var(--indigo)",marginBottom:"1.25rem"}}>Crowd Preference</div>
              <div style={{display:"flex",gap:"1rem",justifyContent:"center",marginBottom:"1.25rem"}}>
                {["low","medium","high"].map(c=>(
                  <div key={c} style={{textAlign:"center"}}>
                    <div style={{width:64,height:64,borderRadius:"50%",background:`${CROWD_C[c]}22`,border:`3px solid ${CROWD_C[c]}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 0.5rem",fontFamily:"'Cormorant Garamond',serif",fontSize:"1.4rem",fontWeight:700,color:CROWD_C[c]}}>{crowdCount[c]||0}</div>
                    <div style={{fontSize:"0.75rem",fontWeight:600,color:"var(--text-mid)",textTransform:"capitalize"}}>{c}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:"0.82rem",color:"var(--text-mid)",textAlign:"center",padding:"0.75rem",background:"var(--bg3)",borderRadius:10}}>
                {topType ? `You love ${topType[0]} destinations most!` : "Save more places to see insights"}
              </div>
            </div>

            {/* Budget distribution */}
            <div style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",padding:"1.5rem",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.2rem",fontWeight:700,color:"var(--indigo)",marginBottom:"1.25rem"}}>Budget Distribution</div>
              {[["Budget","₹1k–5k",saved.filter(p=>p.budget<=5000).length],["Mid-range","₹5k–10k",saved.filter(p=>p.budget>5000&&p.budget<=10000).length],["Premium","₹10k+",saved.filter(p=>p.budget>10000).length]].map(([l,r,n])=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:"0.75rem",marginBottom:"0.75rem"}}>
                  <div style={{width:72,fontSize:"0.72rem",color:"var(--text-mid)",textAlign:"right",flexShrink:0}}><div style={{fontWeight:600,color:"var(--text)"}}>{l}</div><div>{r}</div></div>
                  <div style={{flex:1,height:28,background:"var(--bg3)",borderRadius:8,overflow:"hidden",position:"relative"}}>
                    <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${saved.length?(n/saved.length)*100:0}%`,background:"linear-gradient(90deg,var(--teal),var(--saffron))",borderRadius:8,transition:"width 1s ease"}}/>
                    <div style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--indigo)"}}>{n}</div>
                  </div>
                </div>
              ))}
              <div style={{marginTop:"1rem",padding:"0.75rem",background:"var(--bg3)",borderRadius:10,textAlign:"center"}}>
                <div style={{fontSize:"0.75rem",color:"var(--text-light)"}}>Average budget per destination</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.5rem",fontWeight:700,color:"var(--indigo)"}}>₹{saved.length?Math.round(totalBudget/saved.length).toLocaleString():0}</div>
              </div>
            </div>
          </div>

          {/* Top saved destinations */}
          <div style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",padding:"1.5rem",boxShadow:"var(--shadow-sm)"}}>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.2rem",fontWeight:700,color:"var(--indigo)",marginBottom:"1.25rem"}}>Your Saved Places — Ranked by Score</div>
            <div style={{display:"flex",flexDirection:"column",gap:"0.625rem"}}>
              {[...saved].sort((a,b)=>b.score-a.score).slice(0,8).map((p,i)=>(
                <div key={p.place} style={{display:"flex",alignItems:"center",gap:"1rem",padding:"0.75rem",background:"var(--bg3)",borderRadius:10,border:"1px solid var(--border)"}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:"var(--indigo)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:"0.78rem",flexShrink:0}}>#{i+1}</div>
                  <div style={{fontSize:"1.25rem"}}>{TYPE_EMOJI[p.type]||"🗺️"}</div>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,color:"var(--indigo)",fontSize:"1rem"}}>{p.place}</div>
                    <div style={{fontSize:"0.75rem",color:"var(--text-mid)"}}>{p.state} · {p.type}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,color:"var(--indigo)"}}>₹{p.budget?.toLocaleString()}</div>
                    <div style={{fontSize:"0.72rem",color:"var(--text-light)"}}>★ {p.rating} · 🛡 {p.safety}/10</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Comparison Page ────────────────────────────────────────────────────────────
function ComparisonPage({ toast, onBack }) {
  const [saved, setSaved] = useState([]);
  const [picks, setPicks] = useState([null, null]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/saved-places").then(d => setSaved(d.results || [])).catch(e => toast.add(e.message, "error")).finally(() => setLoading(false));
  }, []);

  const pick = (idx, place) => {
    const p = [...picks]; p[idx] = place; setPicks(p);
  };
  const clear = idx => { const p = [...picks]; p[idx] = null; setPicks(p); };

  const available = (idx) => saved.filter(s => s.place !== picks[idx === 0 ? 1 : 0]?.place);

  const METRICS = [
    ["Rating",      p => `★ ${p.rating}`,                    p => p.rating,       true],
    ["Budget",      p => `₹${p.budget?.toLocaleString()}`,   p => p.budget,       false],
    ["Safety",      p => `${p.safety}/10`,                   p => p.safety,       true],
    ["Days",        p => `${p.days} days`,                   p => p.days,         false],
    ["Temperature", p => `${p.temperature}°C`,               p => p.temperature,  false],
    ["Score",       p => `${p.score?.toFixed(1)}`,           p => p.score,        true],
    ["Crowd",       p => p.crowd,                            p => ({low:3,medium:2,high:1})[p.crowd], true],
    ["Type",        p => `${TYPE_EMOJI[p.type]} ${p.type}`,  null,                null],
    ["State",       p => p.state,                            null,                null],
    ["Best Months", p => p.best_months,                      null,                null],
  ];

  const winner = (metric) => {
    if (!picks[0] || !picks[1] || metric[2] === null) return null;
    const a = metric[2](picks[0]), b = metric[2](picks[1]);
    if (a === b) return "tie";
    return metric[3] ? (a > b ? "left" : "right") : (a < b ? "left" : "right");
  };

  return (
    <div className="page">
      <div className="page-top">
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div>
          <div className="sec-head">⚖️ Compare Destinations</div>
          <div className="sec-sub">Compare two of your saved places side by side</div>
        </div>
      </div>

      {loading ? (
        <div className="sp-center"><div className="spinner sp-dark" style={{width:38,height:38,borderWidth:3}}/></div>
      ) : saved.length < 2 ? (
        <div className="empty">
          <div className="empty-icon">⚖️</div>
          <div className="empty-title">Not enough saved places</div>
          <p className="empty-sub">Save at least 2 destinations from Explore or Recommend to compare them here</p>
        </div>
      ) : (
        <>
          {/* ── Pickers ── */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"2rem",alignItems:"start",marginBottom:"2rem",position:"relative"}}>
  
  {/* VS badge absolutely centered */}
  <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:10}}>
    <div style={{width:48,height:48,borderRadius:"50%",background:"linear-gradient(135deg,var(--saffron),var(--rose))",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:"0.9rem",boxShadow:"0 4px 20px rgba(244,165,53,0.5)",letterSpacing:"0.05em",border:"3px solid var(--bg)"}}>VS</div>
  </div>

  {[0, 1].map(idx => (
    <div key={idx} style={{display:"flex",flexDirection:"column"}}>
      {picks[idx] ? (
        <div style={{background:"linear-gradient(135deg,#1a1a4e,#2d2d7a)",borderRadius:16,padding:"1.5rem",position:"relative",textAlign:"center",boxShadow:"var(--shadow-md)",minHeight:160,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
          <button onClick={() => clear(idx)} style={{position:"absolute",top:"0.75rem",right:"0.75rem",background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",width:28,height:28,borderRadius:"50%",cursor:"pointer",fontSize:"0.85rem"}}>✕</button>
          <div style={{fontSize:"2.5rem",marginBottom:"0.5rem"}}>{TYPE_EMOJI[picks[idx].type]||"🗺️"}</div>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.5rem",fontWeight:700,color:"#fff"}}>{picks[idx].place}</div>
          <div style={{color:"rgba(255,255,255,0.65)",fontSize:"0.82rem",marginTop:4}}>📍 {picks[idx].state}</div>
          <div style={{marginTop:"0.5rem"}}>
            <span style={{background:"rgba(244,165,53,0.2)",border:"1px solid rgba(244,165,53,0.3)",color:"var(--saffron)",fontSize:"0.7rem",fontWeight:600,padding:"0.2rem 0.6rem",borderRadius:100}}>
              {TYPE_EMOJI[picks[idx].type]} {picks[idx].type}
            </span>
          </div>
        </div>
      ) : (
        <div style={{background:"var(--bg2)",borderRadius:16,border:"2px dashed var(--border)",minHeight:160,display:"flex",flexDirection:"column"}}>
          <div style={{padding:"0.875rem 1rem 0.5rem",fontSize:"0.78rem",fontWeight:600,color:"var(--text-mid)",textTransform:"uppercase",letterSpacing:"0.06em",textAlign:"center"}}>
            Choose destination {idx + 1}
          </div>
          <div style={{padding:"0 0.75rem 0.75rem",display:"flex",flexDirection:"column",gap:"0.375rem",overflowY:"auto",flex:1,maxHeight:320}}>
            {available(idx).map(p => (
              <button key={p.place} onClick={() => pick(idx, p)}
                style={{background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:10,padding:"0.65rem 0.75rem",cursor:"pointer",textAlign:"left",transition:"all 0.15s",display:"flex",alignItems:"center",gap:"0.65rem",width:"100%"}}
                onMouseOver={e => { e.currentTarget.style.borderColor="var(--indigo)"; e.currentTarget.style.background="var(--bg2)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.background="var(--bg3)"; }}>
                <div style={{width:34,height:34,borderRadius:9,background:"linear-gradient(135deg,#1a1a4e,#2d2d7a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",flexShrink:0}}>{TYPE_EMOJI[p.type]||"🗺️"}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:"0.88rem",color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.place}</div>
                  <div style={{fontSize:"0.72rem",color:"var(--text-mid)",marginTop:2}}>₹{p.budget?.toLocaleString()} · ★ {p.rating}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  ))}
</div>

          {/* Comparison table */}
          {picks[0] && picks[1] && (
            <div style={{background:"var(--bg2)",borderRadius:"var(--radius)",border:"1px solid var(--border)",overflow:"hidden",boxShadow:"var(--shadow-md)",animation:"fadeUp 0.4s ease"}}>
              {/* Header */}
{/* Header */}
<div style={{display:"grid",gridTemplateColumns:"1fr 120px 1fr",background:"var(--bg3)",borderBottom:"1px solid var(--border)"}}>
  {/* Left place */}
  <div style={{padding:"1rem",borderRight:"1px solid var(--border)",display:"flex",alignItems:"center",gap:"0.75rem"}}>
    <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#1a1a4e,#2d2d7a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.2rem",flexShrink:0}}>{TYPE_EMOJI[picks[0].type]||"🗺️"}</div>
    <div>
      <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.1rem",color:"var(--indigo)"}}>{picks[0].place}</div>
      <div style={{fontSize:"0.72rem",color:"var(--text-mid)",textTransform:"capitalize"}}>{picks[0].type} · {picks[0].state}</div>
    </div>
  </div>
  {/* Center metric label */}
  <div style={{padding:"1rem",borderRight:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg3)"}}>
    <div style={{fontSize:"0.7rem",fontWeight:700,color:"var(--text-mid)",textTransform:"uppercase",letterSpacing:"0.08em"}}>Metric</div>
  </div>
  {/* Right place */}
  <div style={{padding:"1rem",display:"flex",alignItems:"center",gap:"0.75rem",justifyContent:"flex-end"}}>
    <div style={{textAlign:"right"}}>
      <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.1rem",color:"var(--indigo)"}}>{picks[1].place}</div>
      <div style={{fontSize:"0.72rem",color:"var(--text-mid)",textTransform:"capitalize"}}>{picks[1].type} · {picks[1].state}</div>
    </div>
    <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#1a1a4e,#2d2d7a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.2rem",flexShrink:0}}>{TYPE_EMOJI[picks[1].type]||"🗺️"}</div>
  </div>
</div>

              {/* Rows */}
              {METRICS.map((metric) => {
                const [label, fmt] = metric;
                const win = winner(metric);
                return (
                  <div key={label} style={{display:"grid",gridTemplateColumns:"1fr 120px 1fr",borderBottom:"1px solid var(--border)"}}>
                    <div style={{padding:"0.875rem 1rem",display:"flex",alignItems:"center",gap:"0.5rem",borderRight:"1px solid var(--border)",background:win==="left"?"rgba(16,185,129,0.07)":""}}>
                      <div style={{flex:1,fontFamily:"'Cormorant Garamond',serif",fontSize:"1rem",fontWeight:win==="left"?700:400,color:win==="left"?"#065f46":"var(--text)",textTransform:"capitalize"}}>{fmt(picks[0])}</div>
                      {win==="left" && <span style={{color:"#10b981",fontSize:"1.1rem"}}>✓</span>}
                    </div>
                    <div style={{padding:"0.875rem 0.5rem",textAlign:"center",borderRight:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg3)"}}>
                      <div style={{fontSize:"0.7rem",fontWeight:700,color:"var(--text-mid)",textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
                    </div>
                    <div style={{padding:"0.875rem 1rem",display:"flex",alignItems:"center",gap:"0.5rem",background:win==="right"?"rgba(16,185,129,0.07)":""}}>
                      {win==="right" && <span style={{color:"#10b981",fontSize:"1.1rem"}}>✓</span>}
                      <div style={{flex:1,textAlign:"right",fontFamily:"'Cormorant Garamond',serif",fontSize:"1rem",fontWeight:win==="right"?700:400,color:win==="right"?"#065f46":"var(--text)",textTransform:"capitalize"}}>{fmt(picks[1])}</div>
                    </div>
                  </div>
                );
              })}

              {/* Winner banner */}
              <div style={{padding:"1.5rem",textAlign:"center",background:"linear-gradient(135deg,#1a1a4e,#2d2d7a)"}}>
                {(() => {
                  let l = 0, r = 0;
                  METRICS.forEach(m => { const w = winner(m); if(w==="left") l++; if(w==="right") r++; });
                  const overall = l > r ? "left" : r > l ? "right" : "tie";
                  return (
                    <div style={{color:"#fff"}}>
                      <div style={{fontSize:"0.72rem",textTransform:"uppercase",letterSpacing:"0.1em",color:"rgba(255,255,255,0.55)",marginBottom:"0.5rem"}}>Overall Winner</div>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"2rem",fontWeight:700,color:"var(--saffron)"}}>
                        {overall==="tie" ? "🤝 It's a tie!" : overall==="left" ? `🏆 ${picks[0].place}` : `🏆 ${picks[1].place}`}
                      </div>
                      <div style={{fontSize:"0.82rem",color:"rgba(255,255,255,0.6)",marginTop:"0.35rem"}}>{l} vs {r} metrics won</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {(!picks[0] || !picks[1]) && (
            <div className="empty">
              <div className="empty-icon">⚖️</div>
              <div className="empty-title">Select two destinations above</div>
              <p className="empty-sub">Click any saved place from the lists to start comparing</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function HeroSection({ onGetStarted, onExplore, user, destCount }) {
  return (
    <>
      <div className="hero">
        <div className="blob b1"/><div className="blob b2"/><div className="blob b3"/>
        <div className="plane-wrap">
          <div className="plane">✈️</div><div className="plane2">🛫</div>
          <div className="cloud-el c1">☁️</div><div className="cloud-el c2">🌤️</div><div className="cloud-el c3">☁️</div>
        </div>
        <div className="hero-inner">
          <div className="eyebrow">🌏 AI-Powered Travel Discovery</div>
          <h1 className="hero-title">Discover <em>Incredible</em><br/>India — Smarter</h1>
          <p className="hero-sub">Personalised recommendations across {destCount}+ curated destinations. Matched to your budget, season, and travel style.</p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={onGetStarted}>{user?"Start Exploring →":"Get Started Free →"}</button>
            <button className="btn-outline" onClick={onExplore}>{user?"Browse All Places":"See Destinations"}</button>
          </div>
        </div>
        <div className="hero-stats">
          {[[destCount+"+","Destinations"],["8","Trip Types"],["AI","Powered"],["Free","Always"]].map(([n,l])=>(
            <div key={l} className="stat-item"><div className="stat-num">{n}</div><div className="stat-lbl">{l}</div></div>
          ))}
        </div>
      </div>
      <div className="feat-section">
        <div className="feat-inner">
          <div className="feat-title">Why Smart Travel?</div>
          <p className="feat-sub">Everything you need to plan the perfect Indian getaway</p>
          <div className="feat-grid">
            {[["🤖","AI Recommendations","Budget + month = perfect match. ML engine does the rest."],["📍","State Explorer","Browse state by state with sortable ratings, safety and more."],["❤️","Save & Plan","Save favourites and add personal notes to build your wishlist."],["🔒","Secure & Private","JWT auth with OTP email verification. Your data stays safe."],["🌙","Dark Mode","Easy on the eyes whether planning at noon or midnight."],["📱","Works Everywhere","Fully responsive — phone, laptop, or desktop."]].map(([icon,name,desc],i)=>(
              <div key={name} className="feat-card" style={{animationDelay:`${i*80}ms`}}>
                <div className="feat-icon">{icon}</div>
                <div className="feat-name">{name}</div>
                <div className="feat-desc">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="types-section">
        <div className="types-inner">
          <div className="feat-title">Explore by Type</div>
          <p className="feat-sub">8 categories of Indian destinations waiting for you</p>
          <div className="types-row">
            {VALID_TYPES.map(t=><div key={t} className="type-pill" onClick={onGetStarted}>{TYPE_EMOJI[t]} {t.charAt(0).toUpperCase()+t.slice(1)}</div>)}
          </div>
        </div>
      </div>
    </>
  );
}

async function doSave(dest, setList, toast) {
  try{await api("/save-place",{method:"POST",body:JSON.stringify({place:dest.place,note:""})});toast.add(dest.place+" saved! ❤️","success");setList&&setList(ps=>ps.map(d=>d.place===dest.place?{...d,is_saved:true}:d));}
  catch(e){toast.add(e.message,"error");}
}
async function doUnsave(dest, setList, toast) {
  try{const saved=await api("/saved-places");const sp=saved.results.find(s=>s.place===dest.place);if(sp)await api(`/saved-places/${sp.saved_id}`,{method:"DELETE"});toast.add(dest.place+" removed","info");setList&&setList(ps=>ps.map(d=>d.place===dest.place?{...d,is_saved:false}:d));}
  catch(e){toast.add(e.message,"error");}
}

function RecommendPage({ toast, onBack }) {
  const DEF = {budMin:1000,budMax:15000,trip_type:"beach",month:"december",days:4,crowd:""};
  const [f,setF] = useState(DEF);
  const [results,setResults] = useState(null);
  const [loading,setLoading] = useState(false);
  const [selected,setSelected] = useState(null);

  const openDest = (dest) => {
    setSelected(dest);
    try { let r=JSON.parse(localStorage.getItem("st_recent")||"[]"); r=[dest.place,...r.filter(x=>x!==dest.place)].slice(0,8); localStorage.setItem("st_recent",JSON.stringify(r)); } catch{}
  };

  const search = async (af) => {
    const q=af||f;
    if(!q.trip_type||!q.month) return;
    if(!token.get()){toast.add("Session expired","error");return;}
    setLoading(true);setResults(null);
    try{
      const p=new URLSearchParams({budget_min:q.budMin,budget:q.budMax,trip_type:q.trip_type.toLowerCase(),month:q.month.toLowerCase(),days:q.days,top_n:50});
      if(q.crowd)p.append("crowd",q.crowd);
      const data=await api(`/recommend?${p}`);
      const res=data.results||[];
      setResults({...data,results:res,count:res.length});
      if(!res.length)toast.add("No matches — try widening your budget or changing the month","info");
      else toast.add(`Found ${res.length} destination${res.length!==1?"s":""}! 🎉`,"success");
    }catch(e){toast.add("Error: "+e.message,"error");setResults({count:0,results:[]});}
    finally{setLoading(false);}
  };

  useEffect(()=>{search(DEF);},[]);

  return (
    <div className="page">
      <div className="page-top">
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div><div className="sec-head">Find Your Perfect Trip</div><div className="sec-sub">Drag sliders to set your budget range</div></div>
      </div>
      <div className="fbar">
        <div className="fgrid" style={{gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))"}}>
          <div style={{gridColumn:"span 2"}}>
            <label className="flabel">Budget Range (₹)</label>
            <DualRange min={1000} max={50000} valMin={f.budMin} valMax={f.budMax} step={500} onMin={v=>setF({...f,budMin:v})} onMax={v=>setF({...f,budMax:v})}/>
          </div>
          <div><label className="flabel">Trip Type</label>
            <select className="fselect" value={f.trip_type} onChange={e=>setF({...f,trip_type:e.target.value})}>
              {VALID_TYPES.map(t=><option key={t} value={t}>{TYPE_EMOJI[t]} {t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
            </select></div>
          <div><label className="flabel">Travel Month</label>
            <select className="fselect" value={f.month} onChange={e=>setF({...f,month:e.target.value})}>
              {MONTHS.map(m=><option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
            </select></div>
          <div><label className="flabel">Max Days</label>
            <input className="finput" type="number" min={1} max={30} value={f.days} onChange={e=>setF({...f,days:+e.target.value})}/></div>
          <div><label className="flabel">Crowd</label>
            <select className="fselect" value={f.crowd} onChange={e=>setF({...f,crowd:e.target.value})}>
              <option value="">Any crowd</option>
              <option value="low">🟢 Low</option><option value="medium">🟡 Medium</option><option value="high">🔴 High</option>
            </select></div>
          <div style={{alignSelf:"end"}}><button className="fbtn" onClick={()=>search(f)} disabled={loading}>{loading?<span className="spinner"/>:"🔍"} {loading?"Searching…":"Find Destinations"}</button></div>
        </div>
      </div>
      {loading&&<div className="sp-center"><div className="spinner sp-dark" style={{width:38,height:38,borderWidth:3}}/></div>}
      {results&&!loading&&results.count===0&&<div className="empty"><div className="empty-icon">😔</div><div className="empty-title">No matches</div><p className="empty-sub">Try widening your budget range or changing the month</p></div>}
      {results&&!loading&&results.count>0&&(
        <>
          <p className="sec-sub" style={{marginBottom:"1.25rem"}}>Found <strong>{results.count}</strong> destination{results.count!==1?"s":""} — ₹{f.budMin.toLocaleString()} to ₹{f.budMax.toLocaleString()}</p>
          <div className="cards-grid">
            {results.results.map((d,i)=><DestCard key={d.place} dest={d} delay={i*60}
              onSave={dest=>doSave(dest,r=>setResults(prev=>prev?{...prev,results:prev.results.map(x=>x.place===dest.place?{...x,is_saved:true}:x)}:prev),toast)}
              onUnsave={dest=>doUnsave(dest,r=>setResults(prev=>prev?{...prev,results:prev.results.map(x=>x.place===dest.place?{...x,is_saved:false}:x)}:prev),toast)}
              onClick={openDest}/>)}
          </div>
        </>
      )}
      {selected&&<DetailModal dest={selected} onClose={()=>setSelected(null)} onSave={d=>doSave(d,null,toast)} onUnsave={d=>doUnsave(d,null,toast)}/>}
    </div>
  );
}

function ExplorePage({ toast, onBack }) {
  const [places,setPlaces] = useState([]);
  const [loading,setLoading] = useState(false);
  const [searched,setSearched] = useState(false);
  const [f,setF] = useState({trip_type:"",search:"",crowd:""});
  const [selected,setSelected] = useState(null);

  const load = async (af) => {
    const q=af||f;
    if(!token.get()){toast.add("Session expired","error");return;}
    setLoading(true);setSearched(true);
    try{
      const p=new URLSearchParams();
      if(q.trip_type)p.append("trip_type",q.trip_type);
      if(q.search)p.append("search",q.search);
      if(q.crowd)p.append("crowd",q.crowd);
      const qs=p.toString();
      const data=await api(`/places${qs?"?"+qs:""}`);
      setPlaces(data.results||[]);
    }catch(e){toast.add("Error: "+e.message,"error");setPlaces([]);}
    finally{setLoading(false);}
  };

  return (
    <div className="page">
      <div className="page-top">
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div><div className="sec-head">Explore India</div><div className="sec-sub">Search any place or filter by type and crowd</div></div>
      </div>
      <div className="fbar">
        <div className="fgrid">
          <div><label className="flabel">Search Place</label>
            <input className="finput" placeholder="Mumbai, Kerala, beach…" value={f.search} onChange={e=>setF({...f,search:e.target.value})} onKeyDown={e=>e.key==="Enter"&&load({...f})}/></div>
          <div><label className="flabel">Type</label>
            <select className="fselect" value={f.trip_type} onChange={e=>setF({...f,trip_type:e.target.value})}>
              <option value="">All types</option>
              {VALID_TYPES.map(t=><option key={t} value={t}>{TYPE_EMOJI[t]} {t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
            </select></div>
          <div><label className="flabel">Crowd</label>
            <select className="fselect" value={f.crowd} onChange={e=>setF({...f,crowd:e.target.value})}>
              <option value="">Any</option><option value="low">🟢 Low</option><option value="medium">🟡 Medium</option><option value="high">🔴 High</option>
            </select></div>
          <div style={{alignSelf:"end"}}><button className="fbtn" onClick={()=>load({...f})} disabled={loading}>{loading?<span className="spinner"/>:"🔍"} {loading?"Loading…":"Browse"}</button></div>
        </div>
      </div>
      {!searched&&<div className="empty"><div className="empty-icon">🗺️</div><div className="empty-title">Ready to explore?</div><p className="empty-sub">Type a place name or click Browse to see all destinations</p></div>}
      {loading&&<div className="sp-center"><div className="spinner sp-dark" style={{width:38,height:38,borderWidth:3}}/></div>}
      {searched&&!loading&&places.length===0&&<div className="empty"><div className="empty-icon">😔</div><div className="empty-title">No results</div><p className="empty-sub">Try a different name or remove filters</p></div>}
      {searched&&!loading&&places.length>0&&(
        <><p className="sec-sub" style={{marginBottom:"1.25rem"}}>{places.length} destination{places.length!==1?"s":""}</p>
          <div className="cards-grid">{places.map((d,i)=><DestCard key={d.place} dest={d} delay={i*40} onSave={dest=>doSave(dest,setPlaces,toast)} onUnsave={dest=>doUnsave(dest,setPlaces,toast)} onClick={setSelected}/>)}</div></>
      )}
      {selected&&<DetailModal dest={selected} onClose={()=>setSelected(null)} onSave={d=>doSave(d,setPlaces,toast)} onUnsave={d=>doUnsave(d,setPlaces,toast)}/>}
    </div>
  );
}

function StatesPage({ toast, onBack }) {
  const [all,setAll] = useState([]);
  const [selState,setSelState] = useState(null);
  const [sortBy,setSortBy] = useState("rating");
  const [sortDir,setSortDir] = useState("desc");
  const [selected,setSelected] = useState(null);
  const [loading,setLoading] = useState(false);
  const [searched,setSearched] = useState(false);

  const loadAll = async () => {
    if(!token.get()){toast.add("Session expired","error");return;}
    setLoading(true);setSearched(true);
    try{const d=await api("/places");setAll(d.results||[]);}
    catch(e){toast.add("Error: "+e.message,"error");}
    finally{setLoading(false);}
  };

  const groups=all.reduce((a,p)=>{if(!a[p.state])a[p.state]=[];a[p.state].push(p);return a;},{});
  const states=Object.keys(groups).sort();
  const statePlaces=selState?groups[selState]||[]:[];
  const sorted=[...statePlaces].sort((a,b)=>{
    let av=a[sortBy],bv=b[sortBy];
    if(typeof av==="string"){av=av.toLowerCase();bv=bv.toLowerCase();}
    return sortDir==="asc"?(av>bv?1:-1):(av<bv?1:-1);
  });
  const SORTS=[["rating","★ Rating"],["budget","₹ Budget"],["safety","🛡 Safety"],["score","🎯 Score"],["temperature","🌡 Temp"],["days","📅 Days"]];

  return (
    <div className="page">
      <div className="state-hero">
        <div style={{display:"flex",alignItems:"center",gap:"1rem",marginBottom:"0.75rem"}}>
          <button className="back-btn" onClick={selState?()=>setSelState(null):onBack} style={{background:"rgba(255,255,255,0.12)",borderColor:"rgba(255,255,255,0.2)",color:"#fff"}}>
            ← {selState?"All States":"Home"}
          </button>
        </div>
        {selState?(
          <><div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.9rem",fontWeight:700,color:"#fff"}}>{STATE_EMOJI[selState]||"🗺️"} {selState}</div>
          <div style={{color:"rgba(255,255,255,0.6)",fontSize:"0.88rem",marginTop:"0.25rem"}}>{statePlaces.length} destinations · sort by any metric</div></>
        ):(
          <><div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"2rem",fontWeight:700,color:"#fff"}}>📍 Destinations by State</div>
          <div style={{color:"rgba(255,255,255,0.62)",marginTop:"0.25rem",fontSize:"0.9rem"}}>Browse all Indian states and sort destinations by any metric</div></>
        )}
      </div>
      {!searched&&!loading&&<div className="empty"><div className="empty-icon">🗺️</div><div className="empty-title">Explore by State</div><p className="empty-sub" style={{marginBottom:"1.5rem"}}>Load all destinations grouped by state</p><button className="fbtn" style={{margin:"0 auto",display:"inline-flex"}} onClick={loadAll}>🔍 Load All Destinations</button></div>}
      {loading&&<div className="sp-center"><div className="spinner sp-dark" style={{width:38,height:38,borderWidth:3}}/></div>}
      {searched&&!loading&&!selState&&(
        <><p className="sec-sub" style={{marginBottom:"1.25rem"}}>{states.length} states · {all.length} total destinations</p>
          <div className="states-grid">{states.map(s=>(
            <div key={s} className="state-card" onClick={()=>setSelState(s)}>
              <div className="s-icon">{STATE_EMOJI[s]||"🗺️"}</div>
              <div><div className="s-name">{s}</div><div className="s-count">{groups[s].length} destination{groups[s].length!==1?"s":""}</div></div>
            </div>
          ))}</div></>
      )}
      {searched&&!loading&&selState&&(
        <><div className="sort-row"><span className="sort-lbl">Sort:</span>
          {SORTS.map(([k,l])=>(
            <button key={k} className={"sort-btn"+(sortBy===k?" on":"")} onClick={()=>{if(sortBy===k)setSortDir(d=>d==="asc"?"desc":"asc");else{setSortBy(k);setSortDir("desc");}}}>
              {l}{sortBy===k?(sortDir==="desc"?" ↓":" ↑"):""}
            </button>
          ))}</div>
          <div className="cards-grid">{sorted.map((d,i)=><DestCard key={d.place} dest={d} delay={i*50} onSave={dest=>doSave(dest,setAll,toast)} onUnsave={dest=>doUnsave(dest,setAll,toast)} onClick={setSelected}/>)}</div></>
      )}
      {selected&&<DetailModal dest={selected} onClose={()=>setSelected(null)} onSave={d=>doSave(d,setAll,toast)} onUnsave={d=>doUnsave(d,setAll,toast)}/>}
    </div>
  );
}

function SavedPage({ toast, onBack }) {
  const [saved,setSaved] = useState([]);
  const [loading,setLoading] = useState(true);
  const [selected,setSelected] = useState(null);
  const [notes,setNotes] = useState({});

  useEffect(()=>{ api("/saved-places").then(d=>setSaved(d.results||[])).catch(e=>toast.add(e.message,"error")).finally(()=>setLoading(false)); },[]);

  const remove = async (sp) => {
    try{await api(`/saved-places/${sp.saved_id}`,{method:"DELETE"});setSaved(s=>s.filter(x=>x.saved_id!==sp.saved_id));toast.add(sp.place+" removed","info");}
    catch(e){toast.add(e.message,"error");}
  };
  const saveNote = async (sp) => {
    try{await api(`/saved-places/${sp.saved_id}/note`,{method:"PATCH",body:JSON.stringify({note:notes[sp.saved_id]??sp.note})});setSaved(s=>s.map(x=>x.saved_id===sp.saved_id?{...x,note:notes[sp.saved_id]??x.note}:x));toast.add("Note saved!","success");}
    catch(e){toast.add(e.message,"error");}
  };

  return (
    <div className="page">
      <div className="page-top">
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div style={{display:"flex",alignItems:"center",gap:"0.75rem",flexWrap:"wrap"}}>
          <div><div className="sec-head">Saved Places</div><div className="sec-sub">Your personal travel wishlist</div></div>
          <span className="badge">❤️ {saved.length} saved</span>
        </div>
      </div>
      {loading&&<div className="sp-center"><div className="spinner sp-dark" style={{width:38,height:38,borderWidth:3}}/></div>}
      {!loading&&saved.length===0&&<div className="empty"><div className="empty-icon">🤍</div><div className="empty-title">Nothing saved yet</div><p className="empty-sub">Browse destinations and tap ❤️ to save your favourites</p></div>}
      {!loading&&saved.length>0&&(
        <div className="cards-grid">
          {saved.map((sp,i)=>(
            <div key={sp.saved_id} className="dest-card" style={{animationDelay:`${i*60}ms`,cursor:"pointer"}} onClick={()=>setSelected(sp)}>
              <div className="c-head" data-em={TYPE_EMOJI[sp.type]||"🗺️"}>
                <div className="type-badge">{TYPE_EMOJI[sp.type]} {sp.type}</div>
                <div><div className="c-place">{sp.place}</div><div className="c-state">📍 {sp.state}</div></div>
              </div>
              <div className="c-body">
                <div className="chip-row">
                  <span className="chip">₹{sp.budget?.toLocaleString()}</span>
                  <span className="chip">📅 {sp.days}d</span>
                  <span className="chip">★ {sp.rating}</span>
                  <span className="chip"><span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:CROWD_COLOR[sp.crowd],marginRight:2}}/>{sp.crowd}</span>
                </div>
              </div>
              <div className="c-body" style={{paddingTop:0}} onClick={e=>e.stopPropagation()}>
                <label className="flabel">Personal note</label>
                <textarea className="note-area" placeholder="Add a note…" value={notes[sp.saved_id]??sp.note} onChange={e=>setNotes({...notes,[sp.saved_id]:e.target.value})}/>
                <div style={{display:"flex",gap:"0.5rem",marginTop:"0.45rem"}}>
                  <button className="note-save" onClick={()=>saveNote(sp)}>Save note</button>
                  <button onClick={()=>remove(sp)} style={{background:"none",border:"1.5px solid #fecaca",color:"#dc2626",borderRadius:8,padding:"0.45rem 0.9rem",fontSize:"0.78rem",fontWeight:600,cursor:"pointer"}}>Remove</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {selected&&<DetailModal dest={{...selected,is_saved:true}} onClose={()=>setSelected(null)} onSave={()=>{}} onUnsave={d=>{remove(d);setSelected(null);}}/>}
    </div>
  );
}

function ProfilePage({ toast, onLogout, onBack }) {
  const [user,setUser] = useState(null);
  const [savedCount,setSavedCount] = useState(0);
  const [deleting,setDeleting] = useState(false);
  const [showDel,setShowDel] = useState(false);

  useEffect(()=>{ api("/auth/me").then(setUser).catch(()=>{}); api("/saved-places").then(d=>setSavedCount(d.count)).catch(()=>{}); },[]);

  const logout = ()=>{ token.clear(); toast.add("Logged out","info"); onLogout(); };

  const deleteAccount = async () => {
    setDeleting(true);
    try{await api("/auth/delete-account",{method:"DELETE"});token.clear();toast.add("Account permanently deleted","info");onLogout();}
    catch(e){toast.add("Failed to delete: "+e.message,"error");setDeleting(false);}
  };

  return (
    <div className="page">
      <div className="page-top">
        <button className="back-btn" onClick={onBack}>← Home</button>
        <div className="sec-head">My Profile</div>
      </div>
      {!user?<div className="sp-center"><div className="spinner sp-dark" style={{width:38,height:38,borderWidth:3}}/></div>:(
        <div className="prof-card">
          <div className="prof-av">{user.name?.charAt(0).toUpperCase()}</div>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.75rem",fontWeight:700,color:"var(--indigo)"}}>{user.name}</div>
          <div style={{color:"var(--text-mid)",fontSize:"0.9rem",marginTop:"0.25rem"}}>{user.email}</div>
          <div style={{marginTop:"0.75rem"}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:"0.4rem",background:user.is_verified?"#f0fdf4":"#fef3c7",color:user.is_verified?"#16a34a":"#d97706",border:`1px solid ${user.is_verified?"#bbf7d0":"#fde68a"}`,borderRadius:100,padding:"0.22rem 0.72rem",fontSize:"0.78rem",fontWeight:600}}>
              {user.is_verified?"✓ Verified":"⚠ Unverified"}
            </span>
          </div>
          <div style={{display:"flex",gap:"1.5rem",marginTop:"1.5rem",paddingTop:"1.5rem",borderTop:"1px solid var(--border)"}}>
            {[[savedCount,"Places Saved"],["8","Dest. Types"],["94+","Total Places"]].map(([v,l])=>(
              <div key={l}><div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.75rem",fontWeight:700,color:"var(--indigo)"}}>{v}</div><div style={{fontSize:"0.73rem",color:"var(--text-light)"}}>{l}</div></div>
            ))}
          </div>
          <div style={{borderTop:"1px solid var(--border)",marginTop:"1.5rem",paddingTop:"1.5rem",display:"flex",flexDirection:"column",gap:"0.75rem"}}>
            <button onClick={logout}
              style={{background:"none",border:"1.5px solid var(--border)",color:"var(--text-mid)",borderRadius:10,padding:"0.72rem 1.25rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.9rem",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:"0.5rem"}}
              onMouseOver={e=>{e.currentTarget.style.borderColor="#1a1a4e";e.currentTarget.style.color="#1a1a4e";}}
              onMouseOut={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text-mid)";}}>
              🚪 Sign Out
            </button>
            {!showDel?(
              <button onClick={()=>setShowDel(true)}
                style={{background:"none",border:"1.5px solid #fecaca",color:"#dc2626",borderRadius:10,padding:"0.72rem 1.25rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.9rem",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:"0.5rem"}}
                onMouseOver={e=>e.currentTarget.style.background="#fef2f2"}
                onMouseOut={e=>e.currentTarget.style.background="none"}>
                🗑️ Delete Account
              </button>
            ):(
              <div style={{background:"#fef2f2",border:"1.5px solid #fecaca",borderRadius:12,padding:"1.25rem"}}>
                <div style={{fontWeight:600,color:"#dc2626",marginBottom:"0.5rem",fontSize:"0.95rem"}}>⚠️ Are you sure?</div>
                <p style={{fontSize:"0.82rem",color:"#7f1d1d",lineHeight:1.6,marginBottom:"1rem"}}>This will <strong>permanently delete</strong> your account, all saved places, and all your data. This cannot be undone.</p>
                <div style={{display:"flex",gap:"0.625rem"}}>
                  <button onClick={deleteAccount} disabled={deleting}
                    style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.6rem 1.25rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:"0.4rem",opacity:deleting?0.7:1}}>
                    {deleting?<span className="spinner"/>:"🗑️"} {deleting?"Deleting…":"Yes, Delete Forever"}
                  </button>
                  <button onClick={()=>setShowDel(false)}
                    style={{background:"none",border:"1.5px solid #fecaca",color:"#dc2626",borderRadius:8,padding:"0.6rem 1.25rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:600,cursor:"pointer"}}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [authed,setAuthed] = useState(!!token.get());
  const [authView,setAuthView] = useState("login");
  const [page,setPage] = useState("home");
  const [dark,setDark] = useState(()=>localStorage.getItem("theme")==="dark");
  const [user,setUser] = useState(null);
  const [destCount,setDestCount] = useState(94);
  const toast = useToast();

  const toggleDark = ()=>setDark(d=>{localStorage.setItem("theme",!d?"dark":"light");return !d;});

  useEffect(()=>{
    if(authed){ api("/auth/me").then(setUser).catch(()=>{}); }
  },[authed]);

  const handleLogin  = ()=>{setAuthed(true);setPage("recommend");};
  const handleLogout = ()=>{setAuthed(false);setUser(null);setPage("home");};
  const goHome       = ()=>setPage("home");

  const PAGES=[["recommend","✨ Trips"],["explore","🗺 Explore"],["map","📍 Map"],["states","🏛 States"],["saved","❤️ Saved"],["dashboard","📊 Stats"],["compare","⚖️ Compare"]];
  return (
    <>
      <style>{CSS(dark)}</style>
      <nav className="navbar">
        <div className="brand" onClick={goHome}>Smart<span>Travel</span> 🌏</div>
        <div className="nav-links">
          {authed?(
            <>{PAGES.map(([p,l])=><button key={p} className={"nav-btn"+(page===p?" active":"")} onClick={()=>setPage(p)}>{l}</button>)}</>
          ):(
            <><button className="nav-btn" onClick={()=>{setAuthView("login");setPage("auth");}}>Sign In</button>
            <button className="nav-cta" onClick={()=>{setAuthView("register");setPage("auth");}}>Get Started</button></>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"0.5rem",flexShrink:0}}>
          <button className="dark-btn" onClick={toggleDark}>{dark?"☀️ Light":"🌙 Dark"}</button>
          {authed&&user&&(
            <div className="user-pill" onClick={()=>setPage("profile")}>
              <div className="user-av">{user.name?.charAt(0).toUpperCase()}</div>
              <span className="user-name">{user.name?.split(" ")[0]}</span>
            </div>
          )}
        </div>
      </nav>

      {page==="home"&&<HeroSection onGetStarted={()=>authed?setPage("recommend"):(setAuthView("register"),setPage("auth"))} onExplore={()=>authed?setPage("explore"):(setAuthView("login"),setPage("auth"))} user={user} destCount={destCount}/>}
      {page==="auth"&&!authed&&(
        <div className="auth-wrap">
          {authView==="login"
            ?<LoginView onSuccess={handleLogin} onRegister={()=>setAuthView("register")} toast={toast}/>
            :<RegisterView onSuccess={()=>setAuthView("login")} onLogin={()=>setAuthView("login")} toast={toast}/>}
        </div>
      )}
      {authed&&page==="recommend" &&<RecommendPage  toast={toast} onBack={goHome}/>}
      {authed&&page==="explore"   &&<ExplorePage    toast={toast} onBack={goHome}/>}
      {authed&&page==="states"    &&<StatesPage     toast={toast} onBack={goHome}/>}
      {authed&&page==="saved"     &&<SavedPage      toast={toast} onBack={goHome}/>}
      {authed&&page==="dashboard" &&<DashboardPage  toast={toast} onBack={goHome}/>}
      {authed&&page==="compare"   &&<ComparisonPage toast={toast} onBack={goHome}/>}
      {authed&&page==="profile"   &&<ProfilePage    toast={toast} onLogout={handleLogout} onBack={goHome}/>}
      {authed&&page==="map"       &&<MapPage        toast={toast} onBack={goHome}/>}
      <ToastContainer toasts={toast.toasts}/>
    </>
  );
}